import { Category, useAlert } from '@drawbotics/react-drylus';
import { RouterService } from '@tokamakjs/common';
import { Controller, onDidMount, state, useAppContext } from '@tokamakjs/react';
import { GoogleLoginResponse, GoogleLoginResponseOffline } from 'react-google-login';

import { UserLoginResult } from '~/api';
import { AuthService } from '~/services';
import { createTranslate } from '~/utils';

import { AppContext } from '../../types';
import { LoginView } from './login.view';

const tt = createTranslate('routes.login');
const ttErrors = createTranslate('errors');

@Controller({ view: LoginView })
export class LoginController {
  private _appContext = useAppContext<AppContext>();
  private _showAlert = useAlert().showAlert;
  @state searchQueryString: string;
  @state isLoggingIn = false;

  @state isHandlingSuccessfulGoogleLogin = false;
  @state isHandlingSuccessfulMicrosoftLogin = false;

  constructor(private readonly _router: RouterService, private readonly _authService: AuthService) {
    this.searchQueryString = this._router.rawQuery;
  }

  @onDidMount()
  onDidMount() {
    const user = this._authService.getUserFromLocalToken();
    if (user != null) {
      this._appContext.onFinish();
    }
  }

  async login(values: { email: string; password: string }) {
    this.isLoggingIn = true;

    const res = await this._authService.login({
      ...values,
      email: values.email.toLowerCase(),
    });

    if (res === UserLoginResult.WRONG_CREDENTIALS) {
      this.isLoggingIn = false;

      this._showAlert({
        text: tt('credentials_incorrect'),
        category: Category.DANGER,
      });
    } else if (res === UserLoginResult.GENERIC_ERROR) {
      this.isLoggingIn = false;

      this._showAlert({
        text: ttErrors('generic'),
        category: Category.DANGER,
      });
    } else {
      this._appContext.onFinish();
    }
  }

  async handleSuccessfulGoogleLogin(
    googleResponse: GoogleLoginResponse | GoogleLoginResponseOffline,
  ) {
    this.isHandlingSuccessfulGoogleLogin = true;

    const res = await this._authService.handleSuccessfulGoogleLogin(googleResponse);

    if (res.error) {
      if (res.error === 'invalid_user') {
        this._showAlert({
          category: Category.DANGER,
          text: ttErrors('invalid_user'),
        });
      } else {
        this._showAlert({
          text: ttErrors('sign_in_not_available_google'),
          category: Category.DANGER,
        });
      }

      this.isHandlingSuccessfulGoogleLogin = false;
    } else {
      this._appContext.onFinish();
    }
  }

  async handleSuccessfulMicrosoftLogin(code: string) {
    this.isHandlingSuccessfulMicrosoftLogin = true;

    const res = await this._authService.handleSuccessfulMicrosoftLogin(code);

    if (res.error) {
      if (res.error === 'invalid_user') {
        this._showAlert({
          category: Category.DANGER,
          text: ttErrors('invalid_user'),
        });
      } else {
        this._showAlert({
          text: ttErrors('sign_in_not_available_microsoft'),
          category: Category.DANGER,
        });
      }

      this.isHandlingSuccessfulMicrosoftLogin = false;
    } else {
      this._appContext.onFinish();
    }
  }
}
